import type { UseMutationResult } from '@tanstack/react-query'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { QueryStringInput } from '@hello-aria/query-string/dist/stringify'
import { isNil } from 'lodash'
import type { UpdateAttachments } from '@/shared/models/invoiceModels'
import type {
    CreateReceivable,
    Receivable,
    UpdateReceivable,
    ReceivableGateway
} from '@/shared/models/receivableModels'
import {
    createReceivable,
    getReceivable,
    listReceivables,
    updateReceivable
} from '@/shared/services/receivableServices'
import { getPageSize } from '@/shared/utils/queryUtils'
import { useMe } from './useMe'
import type { State } from '@/shared/services/uploadServices'
import { updateReceivableAttachments } from '@/shared/services/uploadServices'

export const receivablesQueryKeys = {
    root: ['receivables'],
    infiniteReceivables: (companyId: string, query?: QueryStringInput): (string | QueryStringInput | undefined)[] => [
        ...receivablesQueryKeys.root,
        companyId,
        query
    ],
    receivableById: (companyId: string, receivableId: string | null | undefined): (undefined | null | string)[] => [
        ...receivablesQueryKeys.root,
        companyId,
        receivableId
    ]
}

type UseInfiniteReceivables = {
    isEnabled?: boolean
    query?: QueryStringInput
}

export const useInfiniteReceivables = (options?: UseInfiniteReceivables) => {
    const { companyId } = useMe()
    const { isEnabled, query } = options ?? {}

    return useInfiniteQuery({
        queryKey: receivablesQueryKeys.infiniteReceivables(companyId, query),

        queryFn: async ({ pageParam }: { pageParam?: string }) => {
            return listReceivables({
                companyId,
                query: {
                    pagination: {
                        after: pageParam,
                        first: getPageSize(query)
                    },
                    ...query
                }
            })
        },
        enabled: isEnabled ?? true,
        getNextPageParam: (lastPage) => {
            if (lastPage?.pagination?.hasNextPage) return lastPage?.pagination?.endCursor
            return undefined
        },
        initialPageParam: undefined
    })
}

export const useReceivableById = (receivableId: string | null | undefined) => {
    const { companyId } = useMe()
    return useQuery({
        queryKey: receivablesQueryKeys.receivableById(companyId, receivableId),
        queryFn: () => getReceivable(companyId, receivableId),
        enabled: !isNil(receivableId)
    })
}

export const useCreateReceivable = (): UseMutationResult<Receivable | undefined, unknown, CreateReceivable> => {
    const { companyId } = useMe()
    return useMutation({
        mutationFn: (payload) => createReceivable(companyId, payload)
    })
}

export const useUpdateReceivable = (): UseMutationResult<Receivable | undefined, unknown, UpdateReceivable> => {
    const { companyId } = useMe()
    return useMutation({
        mutationFn: (payload) => updateReceivable(companyId, payload)
    })
}

export const useUpdateReceivableAttachments = (
    state: State
): UseMutationResult<ReceivableGateway, unknown, UpdateAttachments<Receivable>> => {
    const company = useMe()
    return useMutation({
        mutationFn: (payload) => updateReceivableAttachments(company.companyId, payload, state)
    })
}

export const useInvalidateReceivables = () => {
    const { companyId } = useMe()
    const queryClient = useQueryClient()

    const invalidateReceivables = () => {
        void queryClient.invalidateQueries({ queryKey: receivablesQueryKeys.root })
    }

    const invalidateReceivableById = (receivableId: string) => {
        void queryClient.invalidateQueries({ queryKey: receivablesQueryKeys.receivableById(companyId, receivableId) })
    }

    return {
        invalidateReceivables,
        invalidateReceivableById
    }
}
